// Pure plan-tier helpers — dependency-free so unit tests don't need to
// drag in `window`, React, or the auth context.
//
// Used by:
//   - PlanChangeRequestModal: decides which tile is self-serve vs Contact-sales.
//   - AIUsageSlideOver + ApiKeysTab: gate the Upgrade CTA on PLAN_REQUEST_ROLES.
//   - (Add new callers here as we add plan-aware flows.)

import type { SystemRole } from "./rbac";

export type PlanTier = "free" | "growth" | "scale" | "enterprise";

/**
 * Roles allowed to submit a plan-change request. Must match the backend
 * (services/api/src/routes/plan-change-requests.ts uses requireRole("owner",
 * "admin"), which platform_admin also satisfies) — a CTA shown to anyone else
 * opens a modal whose submit 403s.
 */
export const PLAN_REQUEST_ROLES: SystemRole[] = ["platform_admin", "owner", "admin"];

/** Tier rank — used to compare current vs target. Higher = more expensive. */
export const TIER_RANK: Record<PlanTier, number> = {
  free: 0,
  growth: 1,
  scale: 2,
  enterprise: 3,
};

/**
 * Self-serve only when the target is a paid tier strictly above the current.
 *
 * - Upgrade to a higher paid tier (free->growth, growth->scale): self-serve.
 * - Downgrade (scale->growth, growth->free): NOT self-serve — Contact sales.
 *   Downgrades may involve billing pro-rates and data-retention implications
 *   we don't want a customer to trigger by accident.
 * - Enterprise (any direction): NOT self-serve — Contact sales.
 * - Same plan: NOT actionable (modal shows it as "Current" and disables it).
 */
export function isSelfServeChange(currentPlan: PlanTier, target: PlanTier): boolean {
  if (target === currentPlan) return false;
  if (target === "enterprise") return false;
  return TIER_RANK[target] > TIER_RANK[currentPlan];
}
